import React, { useEffect, useRef } from "react";
import Navbar from '../Components/Navbar';
import { isMobile } from "react-device-detect";
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.css';
import Footer from "../Components/Footer";
import Typed from "typed.js";
import { motion } from "motion/react";
import Core from "../Components/Core";

const Admissions = () => {
  const typedEl = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedEl.current, {
      strings: [
        "Admissions Now Open at Ebun Heritage Schools",
        "Begin Your Child’s Journey to Excellence",
        "From Crèche to College — We Build Futures",
      ],
      typeSpeed: 50,
      backSpeed: 30,
      backDelay: 2000,
      loop: true,
      showCursor: true,
      cursorChar: "<span class='text-warning'>|</span>",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO SECTION */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="bg-white pt-lg-6 pt-4 pb-lg-6 pb-4 px-lg-6 px-4"
        style={{
          backgroundImage: "url('../../assets/images/background/header.jpg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "80% 30%",
          opacity: 0.95,
        }}
      >
        <div className="container">
          <div className="row align-items-center py-lg-8 py-8 px-lg-0">
            <div className="col-lg-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="fs-1 fw-semibold text-dark mb-3"
              >
                <span ref={typedEl}></span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ADMISSION POLICY SECTION */}
      <section className="bg-white pt-lg-6 pt-4 pb-lg-8 pb-8 px-lg-6 px-4">
        <div className="container">
          <div className="row gx-8 py-lg-6 px-lg-0">

            {/* Admission Overview */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Admission Overview
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                Admission into Ebun Heritage Schools is open to qualified
                pupils and students who meet the required age and academic standards.
                We admit learners into our Crèche, Nursery, Kindergarten, Primary,
                and College sections.
                <br /><br />
                Pupils and students are generally admitted between
                <span className="fw-medium"> August and September </span> of each academic year.
              </div>
            </motion.div>

            {/* Age Requirements */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mt-lg-0 mt-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Age Requirements
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                <span className="fw-semibold">Crèche / Playgroup:</span> 3 months – 2 years <br />
                <span className="fw-semibold">Nursery:</span> 3 – 4 years <br />
                <span className="fw-semibold">Kindergarten:</span> 5 years <br />
                <span className="fw-semibold">Primary School:</span> 6 – 11 years <br />
                <span className="fw-semibold">College (Secondary):</span> 11 – 18 years
              </div>
            </motion.div>

            {/* Entrance Examination */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-12 mt-lg-8 mt-6"
            >
              <div className="fs-t2 text-gray-700 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Entrance Examination
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                Admission into JSS1 and SS1 is based on successful performance
                in the College Entrance Examination and interview.
                <br /><br />
                The examination comprises:
                <br /><br />
                1. English Language <br />
                2. Mathematics <br />
                3. General Knowledge
                <br /><br />
                Candidates are expected to attain a required standard in each paper
                before final admission is granted.
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* WAEC / NECO SECTION */}
      <section className="bg-back pt-lg-6 pt-8 pb-lg-6 pb-4 px-lg-6 px-0">
        <div className="container">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="px-lg-18 mt-lg-n14 mt-n8"
          >
            <div className="card shadow-lg bg-dark-blue p-4 p-lg-6" style={{ borderRadius: '30px' }}>
              <div className="text-center text-white">

                <div className="fs-t fw-semibold mb-4">
                  WAEC & NECO Preparation Excellence
                </div>

                <div className="fs-4">
                  Our College section provides intensive preparation for
                  WAEC and NECO examinations.
                  <br /><br />
                  ✔ Structured revision programs <br />
                  ✔ Mock examinations <br />
                  ✔ Continuous assessment tracking <br />
                  ✔ Experienced subject specialists <br />
                  ✔ Moral and academic discipline
                  <br /><br />
                  We prepare our students not just to pass,
                  but to excel with confidence and integrity.
                </div>

              </div>
            </div>
          </motion.div>

          {/* SCHOOL HISTORY */}
          <div className="row align-items-center pt-lg-10 pt-6 pb-lg-6 px-lg-0">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mb-6 mb-lg-0"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Our Growth & Legacy
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                Ebun Heritage Foundation School was established on
                September 25, 2000, with only seventeen pupils in the preschool section.
                <br /><br />
                The school received approval from the Lagos State Ministry of Education
                in 2006 to operate fully as a Nursery and Primary School.
                <br /><br />
                Today, the school has grown to a population of over 400 pupils and students,
                supported by more than 65 teaching and non-teaching staff.
                <br /><br />
                Through dedication, discipline, and commitment to Moral and Academic Excellence,
                the school continues to expand and impact lives positively.
                To God be all the glory.
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 text-center"
            >
              <img
                src="../../assets/images/background/his.svg"
                alt="Ebun Heritage History"
                width={500}
                className="img-fluid mb-lg-4"
              />
            </motion.div>

          </div>
        </div>
      </section>

      <Core />
      <Footer />
    </div>
  );
};

export default Admissions;
